import { Component, EventEmitter, Input, Output, ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { SharedModule } from '@/app/shared/shared.module';
import { interval, Subscription } from 'rxjs';
import {
  BooleanResponseResult,
  RequestPasswordResetCodeCommand,
  UserProxy,
  ValidateUserOTPCommand,
} from '@/app/proxy/proxies';
import { AuthService } from '../../../shared/services/auth.service';
import { OTP_RESEND_COUNTDOWN_SECONDS } from '@/app/shared/consts/otp-consts';

@Component({
  selector: 'auth-verify-otp',
  imports: [SharedModule],
  providers: [UserProxy],
  templateUrl: './verify-otp.component.html',
  styleUrl: './verify-otp.component.less',
})
export class VerifyOtpComponent implements OnInit, OnDestroy {
  @Output() steperChange = new EventEmitter<string>();
  @Input() verifyOtpInfo: string = '';
  @Input() loginInputType: 'email' | 'phone' = 'email';

  otpValue: number | undefined;

  otpValidationStatus: boolean = true;
  userEmail: string = '';
  userPhone: string = '';
  userPhoneCode: string = '';

  resendCodeCounter: number = OTP_RESEND_COUNTDOWN_SECONDS;
  resendCodeCounterSubscription: Subscription = new Subscription();

  constructor(
    private _userProxy: UserProxy,
    private _authService: AuthService,
    private _cd: ChangeDetectorRef
  ) {
    if (this.loginInputType === 'email') {
      this._authService.email$.subscribe((email) => {
        this.userEmail = email || '';
      });
    } else {
      this._authService.phoneNumber$.subscribe((phoneNumber) => {
        this.userPhone = phoneNumber || '';
      });
      this._authService.phoneCode$.subscribe((phoneCode) => {
        this.userPhoneCode = phoneCode || '';
      });
    }
  }

  ngOnInit() {
    // Start the timer immediately when component initializes
    this.startResendCodeTimer();
  }

  ngOnDestroy() {
    // Clean up subscription to prevent memory leaks
    if (this.resendCodeCounterSubscription) {
      this.resendCodeCounterSubscription.unsubscribe();
    }
  }

  backButton() {
    this.steperChange.emit('forgot-pwd');
  }

  confirmVerifyOtp() {
    this._userProxy
      .user_ValidateOTP(
        new ValidateUserOTPCommand({
          id: undefined,
          email: this.userEmail,
          phone: this.userPhone,
          phoneCode: this.userPhoneCode,
          otp: this.otpValue ?? 0,
          otpReason: 3,
        })
      )
      .subscribe((res: BooleanResponseResult) => {
        console.log(res.result);
        if (res.result) {
          this._authService.setOtpNumberForForgotPwd(this.otpValue?.toString() ?? '');
          this.steperChange.emit('change-pwd');
        } else {
          this.otpValue = undefined;
          this.otpValidationStatus = false;
          this._cd.detectChanges();
        }
      });
  }

  resendCodeTrigger() {
    // Only allow resend if counter is 0
    if (this.resendCodeCounter > 0) {
      return;
    }

    this._userProxy
      .user_RequestResetPassword(
        new RequestPasswordResetCodeCommand({
          email: this.loginInputType === 'email' ? this.userEmail : '',
          phone: this.loginInputType === 'phone' ? this.userPhone : '',
          phoneCode: this.loginInputType === 'phone' ? this.userPhoneCode : '',
        })
      )
      .subscribe((res: BooleanResponseResult) => {
        if (res.result) {
          this.resendCodeCounter = OTP_RESEND_COUNTDOWN_SECONDS;
          this.startResendCodeTimer();
        }
      });
  }

  private startResendCodeTimer() {
    // Clear any existing subscription
    if (this.resendCodeCounterSubscription) {
      this.resendCodeCounterSubscription.unsubscribe();
    }

    // Only start timer if counter is greater than 0
    if (this.resendCodeCounter > 0) {
      this.resendCodeCounterSubscription = interval(1000).subscribe(() => {
        this.resendCodeCounter--;
        if (this.resendCodeCounter <= 0) {
          this.resendCodeCounter = 0;
          this.resendCodeCounterSubscription.unsubscribe();
        }
        this._cd.detectChanges(); // Ensure UI updates
      });
    }
  }

  get formattedResendCodeCounter(): string {
    const minutes: number = Math.floor(this.resendCodeCounter / 60);
    const seconds: number = this.resendCodeCounter % 60;
    return `${this.padZero(minutes)}:${this.padZero(seconds)}`;
  }

  private padZero(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }
}
